import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'

import { useAppSelector } from 'hooks/reduxHooks'
import config from 'constants/config'
import {
  getVoucherDisplayConfigs,
  type VoucherDisplayConfig,
  type VoucherDisplayConfigVoucher,
} from 'api/promo'
import VoucherDisplayModel from 'marketing/components/VoucherDisplayModel'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { useLocaleUrlOptions } from 'hooks/useLocaleUrlOptions'
import { buildUrl } from 'lib/url/searchUrlUtils'
import { matchUrlPath, passesAuthState } from './shared/utils'

const VOUCHER_POPUP_QUERY_PARAM = 'vp'
const VOUCHER_POPUP_FORCE_QUERY_PARAM = 'vpf'
const DISMISSED_STORAGE_KEY = 'le-convert-voucher-dismissed'

function isAbsoluteUrl(url: string): boolean {
  return /^https?:\/\//i.test(url)
}

type Match = {
  voucher: VoucherDisplayConfigVoucher
  matchedConfig: VoucherDisplayConfig
  /** Key for the `appear_once` dismissal. */
  dismissalKey: string
}

const DISMISSAL_TTL_MS = 30 * 24 * 60 * 60 * 1000

type DismissalRecord = { key: string; dismissedAt: number }

function readDismissalRecords(): Array<DismissalRecord> {
  try {
    const stored = localStorage.getItem(DISMISSED_STORAGE_KEY)
    if (!stored) return []
    return JSON.parse(stored) as Array<DismissalRecord>
  } catch {
    return []
  }
}

function isVoucherDismissed(key: string): boolean {
  const now = Date.now()
  return readDismissalRecords().some(r => r.key === key && now - r.dismissedAt < DISMISSAL_TTL_MS)
}

function persistVoucherDismissal(key: string): void {
  try {
    const now = Date.now()
    const records = readDismissalRecords().filter(r => now - r.dismissedAt < DISMISSAL_TTL_MS)
    if (!records.some(r => r.key === key)) {
      records.push({ key, dismissedAt: now })
      localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(records))
    }
  } catch {
    // ignore storage errors (private browsing, quota exceeded)
  }
}

function normalizeValue(value: string | undefined | null): string | undefined {
  const normalized = value?.trim().toLowerCase()
  return normalized && normalized.length > 0 ? normalized : undefined
}

function voucherMatchesValue(
  voucher: Pick<VoucherDisplayConfigVoucher, 'voucherName' | 'voucherTitle'>,
  value: string,
): boolean {
  return normalizeValue(voucher.voucherName) === value ||
    normalizeValue(voucher.voucherTitle) === value
}

function firstVoucherByDisplayOrder(vouchers: Array<VoucherDisplayConfigVoucher>): VoucherDisplayConfigVoucher | undefined {
  return vouchers.reduce<VoucherDisplayConfigVoucher | undefined>(
    (first, voucher) => (!first || voucher.displayOrder < first.displayOrder ? voucher : first),
    undefined,
  )
}

function VoucherPopupPrompt() {
  const { search, pathname } = useLocation()
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const { locale, languageOptions } = useLocaleUrlOptions()
  const utmSource = useAppSelector(state => state.utm.source)
  const utmMedium = useAppSelector(state => state.utm.medium)
  const utmCampaign = useAppSelector(state => state.utm.campaign)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const marketingRouterConfig = useAppSelector(state => state.marketing.marketingRouterVoucherDisplayConfig)
  const shownKeyRef = useRef<string | null>(null)
  const promptRef = useRef<HTMLDivElement | null>(null)
  const [show, setShow] = useState(false)
  const isEnabled = config.ENABLE_VOUCHER_DISPLAY_CONFIG

  const pathnameWithoutRegion = useMemo(
    () => pathname.replace(/^\/[a-z]{2}\//i, '/'),
    [pathname],
  )

  const { vpValue, vpfValue } = useMemo(() => {
    const params = new URLSearchParams(search)
    return {
      vpValue: normalizeValue(params.get(VOUCHER_POPUP_QUERY_PARAM)),
      vpfValue: normalizeValue(params.get(VOUCHER_POPUP_FORCE_QUERY_PARAM)),
    }
  }, [search])

  const paramValue = vpValue ?? vpfValue
  const isForced = !vpValue && Boolean(vpfValue)
  const shouldFetch = isEnabled && Boolean(paramValue)

  const { data: displayConfigs = [] } = useQuery({
    queryKey: ['voucher-display-configs', region],
    queryFn: () => getVoucherDisplayConfigs({ region }),
    enabled: shouldFetch,
    staleTime: 5 * 60 * 1000,
  })

  const match: Match | undefined = useMemo(() => {
    // Router config: no vp/vpf, URL or UTM checks. Shows the voucher the param names, else the first.
    if (marketingRouterConfig?.displayType === 'snackbar' && passesAuthState(marketingRouterConfig.authState, isLoggedIn)) {
      const voucher = (paramValue && marketingRouterConfig.vouchers.find(candidate => voucherMatchesValue(candidate, paramValue))) ||
        firstVoucherByDisplayOrder(marketingRouterConfig.vouchers)
      if (voucher) {
        return {
          voucher,
          matchedConfig: marketingRouterConfig,
          dismissalKey: `${voucher.voucherName}:${marketingRouterConfig.voucherDisplayConfigId}`,
        }
      }
    }
    if (!paramValue) return undefined
    for (const cfg of displayConfigs) {
      if (cfg.displayType !== 'snackbar') continue
      if (!passesAuthState(cfg.authState, isLoggedIn)) continue
      if (!isForced) {
        const urlMatch = cfg.urlPaths.some((pattern: string) => matchUrlPath(pattern, pathnameWithoutRegion))
        if (!urlMatch) continue
        if (cfg.source && cfg.source !== utmSource) continue
        if (cfg.medium && cfg.medium !== utmMedium) continue
        if (cfg.campaign && cfg.campaign !== utmCampaign) continue
      }
      const voucher = cfg.vouchers.find(candidate => voucherMatchesValue(candidate, paramValue))
      if (voucher) return { voucher, matchedConfig: cfg, dismissalKey: `${voucher.voucherName}:${paramValue}` }
    }
    return undefined
  }, [displayConfigs, isForced, isLoggedIn, marketingRouterConfig, paramValue, pathnameWithoutRegion, utmCampaign, utmMedium, utmSource])

  useEffect(() => {
    if (!match) return
    const { dismissalKey } = match
    if (shownKeyRef.current === dismissalKey) return
    if (match.matchedConfig.displayMode !== 'persistence' && isVoucherDismissed(dismissalKey)) return
    const timer = window.setTimeout(() => {
      shownKeyRef.current = dismissalKey
      setShow(true)
    }, match.matchedConfig.delayDuration)
    return () => window.clearTimeout(timer)
  }, [match])

  const handleClose = useCallback(() => setShow(false), [])

  const handleDismiss = useCallback(() => {
    if (match && match.matchedConfig.displayMode !== 'persistence') {
      persistVoucherDismissal(match.dismissalKey)
    }
    setShow(false)
  }, [match])

  const ctaText = match?.voucher.voucherCallToAction
  const ctaUrl = match?.voucher.voucherCallToActionUrl
  // Absolute URLs render as a plain anchor (href); internal paths get regionalised and
  // render as an in-app link (to).
  const isExternalCta = !!ctaUrl && isAbsoluteUrl(ctaUrl)
  const ctaHref = isExternalCta ? ctaUrl : undefined
  const ctaTo = ctaUrl && !isExternalCta
    ? buildUrl(ctaUrl, region, undefined, undefined, { locale, languageOptions })
    : undefined

  if (!match) return null

  return (
    <VoucherDisplayModel
      show={show}
      promptRef={promptRef}
      voucher={match.voucher}
      displayMode={match.matchedConfig.displayMode}
      ctaText={ctaText}
      ctaTo={ctaTo}
      ctaHref={ctaHref}
      onClose={handleClose}
      onDismiss={handleDismiss}
      onCtaClick={handleClose}
    />
  )
}

export default VoucherPopupPrompt
